import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import FailedIcon from 'icons/exclamation-circle.svg';
import ProgressIcon from 'icons/progress.svg';
import { useTranslation } from 'react-i18next';

import FilledButton from 'components/application/buttons/FilledButton';
import DownloadModal from 'components/application/DownloadModal';

import DocumentGeneratorService from 'components/helpers/DocumentGeneratorService';

export default function DocumentGenerator({
  generateParams,
  generateUrl,
  isGenerating,
  isInline,
  modalFailureMessage,
  modalHeader,
  modalSuccessMessage,
  setIsGenerating,
}) {
  const [docUrl, setDocUrl] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const [documentGeneratorService, setDocumentGeneratorService] =
    useState(null);

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const reset = () => {
    if (documentGeneratorService) {
      documentGeneratorService.end();
    }

    setIsModalOpen(false);
    setIsReady(false);
    setIsFailed(false);
    setIsButtonDisabled(false);
    setDocumentGeneratorService(null);
    setDocUrl('');
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const successCallback = (url) => {
    setDocUrl(url);
    setIsButtonDisabled(false);
    setIsReady(true);
  };

  const failureCallback = () => {
    setIsFailed(true);
    setIsButtonDisabled(false);
  };

  const startGenerating = () => {
    const newDocumentGeneratorService = new DocumentGeneratorService({
      generateUrl,
      generateParams,
      successCallback,
      failureCallback,
      isInline,
    });
    newDocumentGeneratorService.start();
    setIsButtonDisabled(true);
    setIsFailed(false);
    setDocumentGeneratorService(newDocumentGeneratorService);
  };

  const openDocument = () => {
    window.location.href = docUrl;
    reset();
  };

  const modalBody =
    isReady ? modalSuccessMessage
    : isFailed ? modalFailureMessage
    : i18nTShared('downloading...');

  const modalBodyIcon =
    isFailed ? <FailedIcon className='.icon--within-text' /> : '';

  const modalButton = (
    <FilledButton
      className={classNames(
        isButtonDisabled ?
          'modal_download_button--disabled'
        : 'modal_download_button',
      )}
      color='mint'
      disabled={isButtonDisabled}
      onClick={isFailed ? startGenerating : openDocument}
    >
      <ProgressIcon
        className={classNames(isReady || isFailed ? 'hide' : 'tw-mr-1')}
        height={18}
        width={18}
      />
      {isReady ?
        i18nTShared('downloadNow')
      : isFailed ?
        i18nTShared('retry')
      : i18nTShared('pleaseWait...')}
    </FilledButton>
  );

  useEffect(() => {
    if (isGenerating) {
      openModal();
      startGenerating();
    }
    return () => {
      setIsGenerating(false);
    };
  }, [isGenerating]);

  return (
    <DownloadModal
      actionButton={modalButton}
      body={modalBody}
      bodyIcon={modalBodyIcon}
      header={modalHeader}
      isFailed={isFailed}
      isLandscape={true}
      isOpen={isModalOpen}
      onClose={reset}
    />
  );
}
